import type { ParseKeys, TFunction } from 'i18next'
import {
  Banknote,
  Circle,
  CircleCheck,
  FileText,
  History,
  MessageSquare,
  Paperclip,
  Pencil,
  Route,
  Search,
  TriangleAlert,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SuppressedValue } from '@/components/data/SuppressedValue'
import { Banner } from '@/components/feedback/Banner'
import { EmptyState } from '@/components/feedback/EmptyState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { useFormat } from '@/i18n/useFormat'
import { ACTION_LIMIT, AUDIT_LIMIT, useCaseHistory } from '@/features/cases/api/useCaseHistory'
import { isSuppressed, type CaseHistoryEvent } from '@/types/domain'

/* The case history feed (FR-37, AD-6, UX-DR-12).
 *
 * ONE list over two sources — the action log and the financial audit trail —
 * with nothing in the layout separating them. The question a reader arrives
 * with is "when did this change and who did it", never "which of the two tables
 * holds it", and a screen split in two would make them ask the second question
 * before they could answer the first. The merge itself is in useCaseHistory,
 * which also says why it happens here rather than on the server.
 *
 * THE ACTOR'S ROLE IS NOT SHOWN, and this is a deliberate departure from the
 * mockup, whose third line reads "Aygul B. · Operations". Do not "fix" it.
 * Neither source carries a role: the action log denormalises the actor's NAME
 * at write time, on purpose, so that no reader joins into identity and renaming
 * an account cannot rewrite history. A role resolved at read time would be
 * TODAY's role printed beside a HISTORICAL act — the colleague who was in
 * Operations in July and is in Finance now would have their July entries
 * relabelled, which is the one thing an audit trail must never do. The name is
 * what the sources know, so the name is what the feed prints.
 *
 * The two ENTRY KINDS render differently, because they are not the same fact:
 * an act carries no old→new values (the log stores ids and statuses only, which
 * is exactly why it needs no suppression), while a field transition carries
 * both — through SuppressedValue, so a withheld one reads as withheld rather
 * than as a field nobody ever touched.
 */

/* ── The action dictionary ────────────────────────────────────────────────── */

/** The `case.` the log prefixes every one of its keys with. Stripped before the
 *  lookup, so the catalogue can nest `actions.status.change` exactly as the key
 *  reads — one shape on both sides, no mapping table to keep in step. */
const ACTION_PREFIX = 'case.'

/** What an action entry says it is, in the reader's language.
 *
 *  THE RAW KEY IS THE FALLBACK, and it is not optional. `backend/cmd/asa/
 *  actions.go` is a live file: a route mounted next month starts writing its
 *  own action the day it ships, and this catalogue is not extended in the same
 *  breath. That day should produce a slightly ugly line — "case.claim.open" —
 *  rather than a blank one, because a blank is indistinguishable from a bug and
 *  an ugly key is still present, searchable and unmistakably an entry.
 *
 *  `returnObjects` is what makes the second way a key can miss survivable: an
 *  action naming a BRANCH of the dictionary rather than a leaf ("case.leg")
 *  resolves to an object, and without it i18next renders that as its own
 *  diagnostic sentence — "key 'cases…leg' returned an object instead of
 *  string" — in the middle of the feed. Asking for the object and refusing
 *  anything that is not a string puts the raw key there instead. */
function actionTitle(action: string, t: TFunction): string {
  if (!action.startsWith(ACTION_PREFIX)) return action

  const key = `cases.history.actions.${action.slice(ACTION_PREFIX.length)}`
  const title = t(key as ParseKeys, { defaultValue: action, returnObjects: true })
  return typeof title === 'string' ? title : action
}

/* Icons GROUP BY PREFIX rather than one per key: thirty distinct glyphs down
 * the left of a feed is thirty things to learn and nothing to read. Eight
 * families is a rail somebody can skim for "the money ones".
 *
 * A family is named rather than being a glyph in the table below, so the
 * grouping survives somebody swapping a lucide icon for another. */
const FAMILY_ICONS = {
  record: Pencil,
  status: CircleCheck,
  routing: Route,
  people: Users,
  file: Paperclip,
  message: MessageSquare,
  money: Banknote,
  invoice: FileText,
  /** No family matched — an action this build has never heard of. It still
   *  gets a rail position: the neutral dot is what keeps the spine straight
   *  for the entry whose translation is missing too. */
  unknown: Circle,
} as const satisfies Record<string, LucideIcon>

type IconFamily = keyof typeof FAMILY_ICONS

/* Ordered longest-prefix-first where two families share a stem, so
   `case.status-update.send` cannot be captured by `case.status.`. */
const ACTION_FAMILIES: readonly (readonly [prefix: string, family: IconFamily])[] = [
  ['case.create', 'record'],
  ['case.update', 'record'],
  ['case.reopen', 'record'],
  ['case.status-update.', 'status'],
  ['case.status.', 'status'],
  ['case.leg.', 'routing'],
  ['case.participant.', 'people'],
  ['case.team.', 'people'],
  ['case.file.', 'file'],
  ['case.message.', 'message'],
  ['case.chat.', 'message'],
  ['case.rate.', 'money'],
  ['case.expense.', 'money'],
  ['case.payment.', 'money'],
  ['case.tranche.', 'money'],
  ['case.invoice.', 'invoice'],
]

function eventFamily(event: CaseHistoryEvent): IconFamily {
  // Every audit entry is a field being edited, whichever record it sits on, so
  // the trail has one family rather than a second table to keep in step with
  // finance's field names.
  if (event.source === 'audit') return 'record'

  const match = ACTION_FAMILIES.find(([prefix]) => event.entry.action.startsWith(prefix))
  return match ? match[1] : 'unknown'
}

/* ── Filtering ────────────────────────────────────────────────────────────── */

type TimeWindow = 'all' | 'today' | 'yesterday' | 'week'

const TIME_WINDOWS = [
  { value: 'all', labelKey: 'cases.history.filters.all' },
  { value: 'today', labelKey: 'cases.history.filters.today' },
  { value: 'yesterday', labelKey: 'cases.history.filters.yesterday' },
  { value: 'week', labelKey: 'cases.history.filters.week' },
] as const satisfies readonly { value: TimeWindow; labelKey: ParseKeys }[]

function startOfDay(date: Date): Date {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)
  return start
}

/** The start of the local day N days back, by CALENDAR arithmetic rather than
 *  by subtracting 24h in milliseconds.
 *
 *  A day is not always 86 400 000 ms: in a zone that observes daylight saving,
 *  two days a year are an hour longer or shorter. Fixed-millisecond arithmetic
 *  lands an hour off local midnight on exactly those days, and an entry made in
 *  the hour next to midnight falls into the neighbouring window — Yesterday
 *  showing this morning's act, or Today losing it. */
function startOfDaysAgo(now: Date, days: number): Date {
  const start = startOfDay(now)
  start.setDate(start.getDate() - days)
  return start
}

/** Whether an instant falls in the chosen window, in the READER's zone.
 *
 *  Local day boundaries rather than UTC ones: "today" means the day the person
 *  looking at the screen is having. The two offices are three hours apart, and
 *  a feed that answered UTC's question would show an Ashgabat morning's work
 *  under Yesterday for the first three hours of every day.
 *
 *  "This week" is a ROLLING SEVEN DAYS, not the calendar week, and that is a
 *  choice worth naming: a calendar week resets every Monday morning, so the
 *  filter somebody reaches for to ask "what has been happening lately" answers
 *  "almost nothing" on exactly the morning they most need it. */
function inWindow(at: string, chosen: TimeWindow, now: Date): boolean {
  if (chosen === 'all') return true

  const instant = new Date(at).getTime()
  const today = startOfDay(now).getTime()

  if (chosen === 'today') return instant >= today
  if (chosen === 'yesterday') {
    const yesterday = startOfDaysAgo(now, 1).getTime()
    return instant >= yesterday && instant < today
  }
  return instant >= startOfDaysAgo(now, 6).getTime()
}

/** Everything of an entry a search may reach: what is rendered, and only what
 *  is rendered.
 *
 *  A SUPPRESSED VALUE IS NOT SEARCHABLE, and that is a rule rather than an
 *  oversight. Matching against a value AD-6 withheld would turn the search box
 *  into an oracle: type a guess, watch the row appear or not, and the hidden
 *  vendor is read out one candidate at a time. */
function searchText(event: CaseHistoryEvent, t: TFunction): string {
  if (event.source === 'action') {
    return [actionTitle(event.entry.action, t), event.entry.actorName].join(' ')
  }

  const { field, actorName, oldValue, newValue, reason } = event.entry
  const visible = [oldValue, newValue, reason].filter(
    (value): value is string => value !== undefined && !isSuppressed(value),
  )
  return [field, actorName, ...visible].join(' ')
}

/* ── The section ──────────────────────────────────────────────────────────── */

export function CaseHistory({ caseId }: { caseId: string }) {
  const { t } = useTranslation()
  const format = useFormat()
  const { events, actions, audit, auditTruncated, actionsTruncated } = useCaseHistory(caseId)

  const [timeWindow, setTimeWindow] = useState<TimeWindow>('all')
  const [query, setQuery] = useState('')

  const visible = useMemo(() => {
    // `now` is read once per filter pass rather than per entry: a comparison
    // against a clock that moves inside the loop can put two entries a
    // millisecond apart on opposite sides of midnight.
    const now = new Date()
    const needle = query.trim().toLowerCase()

    return events.filter(
      (event) =>
        inWindow(event.at, timeWindow, now) &&
        (needle === '' || searchText(event, t).toLowerCase().includes(needle)),
    )
  }, [events, timeWindow, query, t])

  /* The two sources are asked about SEPARATELY throughout, which is the whole
     reason they are two queries: one being dead must cost the reader the other
     source's entries and nothing more. The error banners below are per-source
     for exactly that.

     The SKELETON is the one thing they share, and it waits for BOTH — `||`, not
     `&&`. The feed is a merge, and a merge missing half its input is not a
     shorter answer but a wrong one: with `&&`, a case whose faster source came
     back empty (or refused) would show "Nothing has happened yet" and a count of
     zero over a trail still in flight, then rewrite both a moment later. A
     section that states an answer it does not have yet is worse than one that
     admits it is still reading. */
  const loading = actions.isPending || audit.isPending
  const filtered = timeWindow !== 'all' || query.trim() !== ''

  return (
    <Card>
      <CardHeader
        action={
          <SegmentedControl
            options={TIME_WINDOWS}
            value={timeWindow}
            onChange={setTimeWindow}
            label={t('cases.history.title')}
          />
        }
      >
        <CardTitle>{t('cases.history.title')}</CardTitle>
        {/* The count of what is ON SCREEN, not of what was fetched. A header
            saying 40 over a filtered list of three is the header disagreeing
            with the list, and the list is the thing being read — and nothing at
            all while the two reads are in flight, because "0 events" over a
            loading skeleton is an answer the section does not have yet. */}
        {loading ? null : (
          <p className="text-text-muted text-[12.5px] leading-snug font-light">
            {t('cases.history.count', { count: visible.length })}
          </p>
        )}
      </CardHeader>

      <CardBody className="flex flex-col gap-3 pb-0">
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onClear={() => setQuery('')}
          iconLeft={Search}
          inputSize="sm"
          placeholder={t('cases.history.searchPlaceholder')}
          aria-label={t('cases.history.searchPlaceholder')}
        />

        {/* A refused source names ITSELF. "Something went wrong" over half a
            feed leaves the reader unable to tell whether the missing half is
            missing or never existed — which is how "nobody ever changed that"
            gets said out loud. */}
        {actions.isError ? (
          <Banner
            tone="attention"
            title={t('cases.history.loadFailed.actions')}
            action={
              <Button variant="secondary" size="sm" onClick={() => void actions.refetch()}>
                {t('cases.history.loadFailed.retry')}
              </Button>
            }
          />
        ) : null}
        {audit.isError ? (
          <Banner
            tone="attention"
            title={t('cases.history.loadFailed.audit')}
            action={
              <Button variant="secondary" size="sm" onClick={() => void audit.refetch()}>
                {t('cases.history.loadFailed.retry')}
              </Button>
            }
          />
        ) : null}
      </CardBody>

      {loading ? (
        <CardBody>
          <Skeleton className="h-24 w-full rounded-md" />
        </CardBody>
      ) : visible.length === 0 ? (
        /* TWO empty states, and the difference is substantive: a case nothing
           has happened to has nothing to clear, while a filter that matched
           nothing does — so only one of the two may offer to clear it. */
        filtered ? (
          <EmptyState
            icon={Search}
            title={t('cases.history.noMatchTitle')}
            description={t('cases.history.noMatchDescription')}
          />
        ) : actions.isError || audit.isError ? (
          /* A THIRD state, for the same reason the skeleton waits for both
             sources: with one of them refused, "Nothing has happened yet" is
             an answer the section does not have. Half the history is not
             empty, it is unknown — and the banner above says which half. */
          <EmptyState
            icon={TriangleAlert}
            title={t('cases.history.unknownTitle')}
            description={t('cases.history.unknownDescription')}
          />
        ) : (
          <EmptyState
            icon={History}
            title={t('cases.history.emptyTitle')}
            description={t('cases.history.emptyDescription')}
          />
        )
      ) : (
        <>
          {/* The entries hang off a hairline spine — a log is a sequence, and a
              spine says so more quietly than a fence of row borders. */}
          <ul className="flex flex-col px-[18px] py-4">
            {visible.map((event, index) => (
              <HistoryRow
                key={`${event.source}-${event.entry.id}`}
                event={event}
                last={index === visible.length - 1}
                timestamp={format.timestamp(event.at)}
              />
            ))}
          </ul>

          {/* Said out loud, and only when it is true. A feed that quietly stops
              at five hundred looks exactly like a feed that has five hundred
              entries in it.

              ONE NOTICE PER SOURCE, because one cap biting says nothing about
              the other: the trail's is the limit this client asks for and the
              log's is the one the server imposes, and a case can easily be over
              one and under the other. */}
          {actionsTruncated || auditTruncated ? (
            <div className="text-text-muted border-line-hair flex flex-col gap-0.5 border-t px-[18px] py-2.5 text-[12px] leading-snug font-light">
              {actionsTruncated ? (
                <p>{t('cases.history.truncated.actions', { limit: ACTION_LIMIT })}</p>
              ) : null}
              {auditTruncated ? (
                <p>{t('cases.history.truncated.audit', { limit: AUDIT_LIMIT })}</p>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </Card>
  )
}

function HistoryRow({
  event,
  last,
  timestamp,
}: {
  event: CaseHistoryEvent
  last: boolean
  timestamp: string
}) {
  const { t } = useTranslation()
  const Icon = FAMILY_ICONS[eventFamily(event)]
  const title =
    event.source === 'action'
      ? actionTitle(event.entry.action, t)
      : // The trail names its fields in the machine's words, which is what lets
        // one entry be read in two languages. Printed as it arrives until the
        // catalogue has words for them — a raw field name is honest, and it is
        // what this section has always shown.
        event.entry.field

  return (
    <li className="group flex gap-3">
      <div className="flex flex-col items-center">
        <span className="border-neutral-300 bg-surface text-text-muted mt-px flex size-[22px] shrink-0 items-center justify-center rounded-full border">
          <Icon size={11} aria-hidden />
        </span>
        {!last ? <span aria-hidden className="bg-line w-px flex-1" /> : null}
      </div>

      <div className="group-last:pb-0 flex min-w-0 flex-1 flex-col gap-1 pb-4">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 leading-snug">
          <span className="text-text-heading text-[13px]">{title}</span>
          <span className="text-text-muted text-[12px] font-light">{timestamp}</span>
        </div>

        {/* Only the trail has a transition to show. An act has none, and
            inventing one — "status → stuck" from the entry's annotation — would
            print a value the log never promised to carry. */}
        {event.source === 'audit' ? (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[12px] leading-snug font-light">
            <SuppressedValue
              value={event.entry.oldValue}
              label={t('common.suppressed.generic')}
              className="text-text-muted"
            />
            {/* A separator glyph: decorative, and the two values it sits
                between carry the meaning. */}
            <span className="text-text-faint" aria-hidden>
              →
            </span>
            <SuppressedValue
              value={event.entry.newValue}
              label={t('common.suppressed.generic')}
              className="text-text-heading"
            />
          </div>
        ) : null}

        {event.source === 'audit' && event.entry.reason !== undefined ? (
          <div className="text-text-muted flex flex-wrap items-baseline gap-x-1.5 text-[12px] leading-snug font-light">
            <span>{t('cases.history.audit.reason')}</span>
            <SuppressedValue
              value={event.entry.reason}
              label={t('common.suppressed.generic')}
            />
          </div>
        ) : null}

        <span className="text-text-body text-[12px] leading-snug font-light">
          {event.entry.actorName}
        </span>
      </div>
    </li>
  )
}
